import * as React from "react";
import Box from "@mui/joy/Box";
import Loading from "./Utils/Loading";
import Grid from "@mui/joy/Grid";
import CustomDashboardCard from "./Cards/CustomDashboardCard";
import { UserAuth } from "../context/AuthContext";
import { db } from "../FirebaseConfig";
import NumbersIcon from "@mui/icons-material/Numbers";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import { Card, CardContent, Link, Typography } from "@mui/joy";
import LineChart from "./Charts/LineChart";
import PieChart from "./Charts/PieChart";
import CustomEventCard from "./Cards/CustomEventCard";
import CardCover from "@mui/joy/CardCover";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import { Link as RouterLink } from "react-router-dom";

interface Data {
  total_challenges_solved?: number;
}

export default function Home() {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState<any>({});
  const [points, setPoints] = React.useState<any>({});
  const [rank, setRank] = React.useState<any>({});
  const { userData } = UserAuth();
  const [eventdata, setEventData] = React.useState<any>([]);

  // Fetch user-related data (challenges solved, points, rank)
  React.useEffect(() => {
    const getData = async () => {
      if (!userData || !userData?.id) {
        return;
      }
      setLoading(true);
      try {
        const querySnapshot = await db
          .collection("ChallengeSubmissions")
          .where("userid", "==", userData?.id)
          .get();

        const total_challenges_solved = querySnapshot.size;
        setData({ total_challenges_solved });

        const total_points = querySnapshot.docs.reduce((acc, doc) => {
          return acc + parseInt(doc.data().points);
        }, 0);
        setPoints({ total_points });

        const allSubmissionsSnapshot = await db.collection("ChallengeSubmissions").get();

        const userPoints: Record<string, number> = {};
        allSubmissionsSnapshot.forEach((doc) => {
          const userid = doc.data().userid;
          const points = parseInt(doc.data().points);
          userPoints[userid] = (userPoints[userid] || 0) + points;
        });
        
        // Sort the userPoints to determine ranks
        const sortedUserIds = Object.keys(userPoints).sort(
          (a, b) => userPoints[b] - userPoints[a]
        );
        const rank =
          sortedUserIds.findIndex((userid) => userid === userData?.id) + 1;
        setRank({ rank });
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    getData();
  }, [userData]);

  // Fetch event data
  React.useEffect(() => {
    const getEventData = async () => {
      setLoading(true);
      try {
        const querySnapshot = await db
          .collection("Events")
          .where("live", "==", true)
          .orderBy("enddate", "asc")
          .get();
        const eventData = querySnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setEventData(eventData);
      } catch (err) {
        // handle error
      } finally {
        setLoading(false);
      }
    };
    getEventData();
  }, []);

  // Update live status of events based on end date in real time
  React.useEffect(() => {
    const updateLiveStatusBasedOnEndDate = async () => {
      const now = new Date();
      let updatesNeeded = false;

      try {
        const updatedEvents = await Promise.all(
          eventdata.map(async (event: any) => {
            const endDate = new Date(event.enddate);
            if (endDate <= now && event.live) {
              const docRef = db
                .collection("Events")
                .doc(event.eventname.toLowerCase());
              try {
                const docSnapshot = await docRef.get();
                if (docSnapshot.exists) {
                  await docRef.update({ live: false });
                  updatesNeeded = true;
                  return { ...event, live: false };
                }
              } catch (err) {
                return event;
              }
            }
            return event;
          })
        );

        if (updatesNeeded) {
          setEventData(updatedEvents); // Update the state with the updated live status
        }
      } catch (error) {
        // handle error
      }
    };

    // Set an interval to update the live status every 10 seconds
    const intervalId = setInterval(updateLiveStatusBasedOnEndDate, 10000);
    updateLiveStatusBasedOnEndDate(); // Initial check on component mount

    return () => {
      clearInterval(intervalId);
    };
  }, [eventdata]); // Only run when eventdata changes

  // Filter events that have ended
  const filteredEvents = React.useMemo(() => {
    const now = new Date();
    return eventdata.filter((event: any) => {
      const endDate = new Date(event.enddate);
      return endDate > now; // Only show events that are still live
    });
  }, [eventdata]);

  return loading ? (
    <Loading />
  ) : (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid xs={4}>
          <CustomDashboardCard
            title="Challenges Solved"
            color="primary"
            icon={<AssignmentTurnedInIcon />}
            value={data.total_challenges_solved}
          />
        </Grid>
        <Grid xs={4}>
          <CustomDashboardCard
            title="Score"
            color="danger"
            icon={<ScoreboardIcon />}
            value={points.total_points}
          />
        </Grid>
        <Grid xs={4}>
          <CustomDashboardCard
            title="Ranking"
            color="success"
            icon={<NumbersIcon />}
            value={rank.rank}
          />
        </Grid>
      </Grid>
      <div className="pt-4"></div>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid xs={6}>
          <Card
            invertedColors
            sx={{
              borderRadius: "sm",
              p: 2,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Typography level="h2"> Progress </Typography>
            </CardContent>
            <LineChart />
          </Card>
        </Grid>
        <Grid xs={6}>
          <Card
            invertedColors
            sx={{
              borderRadius: "sm",
              p: 2,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Typography level="h2">Challenges</Typography>
              <PieChart />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <div className="pt-4"></div>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid xs={12}>
          <Card sx={{ flexGrow: 1 }}>
            <CardCover>
              <img src="/assets/Live-Event.jpg" loading="lazy" alt="" />
            </CardCover>
            <CardContent orientation="horizontal">
              <Typography
                level="h1"
                fontWeight="lg"
                textColor="#fff"
                startDecorator={<LiveTvIcon style={{ color: "#fff" }} />}
              >
                <strong>Live Events</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <div className="pt-4"></div>
      {filteredEvents.length > 0 ? (
        <Box sx={{ flex: 1, width: "100%" }}>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {filteredEvents.map((data: any) => (
              <Grid xs={4} key={data.id}>
                <CustomEventCard
                  title={data.eventname}
                  category={data.category}
                  banner={data.fileUrl}
                  
                  data={data}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Typography level="h4" textAlign="center" sx={{ mt: 2, mb: 2 }}>
          No live events are available at the moment. Stay tuned and practice
          more on{" "}
          <Link component={RouterLink} to="/challenges">
            Challenges.
          </Link>
        </Typography>
      )}
      <div className="pt-4"></div>
    </Box>
  );
}
